import { css, type CSSObject } from '@emotion/react'
import {
  FocusIndicator,
  Spacing,
  Theme,
  ThemedColors,
  Transition,
} from '@opallabs/gem'
import { screenreaderOnly } from '../utils/a11y-styles'

const classNamePrefix = 'lapidary-switch--'
export const selectors = {
  container: `${classNamePrefix}container`,
  handle: `${classNamePrefix}handle`,
  label: `${classNamePrefix}label`,
  tray: `${classNamePrefix}tray`,
} as const

const TRAY_BLOCK_SIZE = Spacing.S
const TRAY_INLINE_SIZE = Spacing.L
const INDICATOR_SIZE = Spacing.M

export const container = css({
  alignItems: 'center',
  appearance: 'none',
  backgroundColor: 'transparent',
  blockSize: INDICATOR_SIZE,
  border: 'initial',
  display: 'flex',
  inlineSize: TRAY_INLINE_SIZE,
  padding: 0,
  position: 'relative',

  '&:focus': {
    // Handled by `containerState()`
    outline: 'none',
  },
  '&:disabled': {
    opacity: 0.7,
  },

  [`.${selectors.handle}`]: {
    backgroundColor: `var(${ThemedColors.Surface4})`,
    blockSize: INDICATOR_SIZE,
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    inlineSize: INDICATOR_SIZE,
    insetBlock: 0,
    insetInlineStart: 0,
    position: 'absolute',
    ...Transition(['backgroundColor', 'insetInlineStart'], {
      motionProperties: ['backgroundColor', 'insetInlineStart'],
    }),
  },
  [`&[aria-checked=true] .${selectors.handle}`]: {
    backgroundColor: `var(${ThemedColors.Primary6})`,
    insetInlineStart: TRAY_INLINE_SIZE - INDICATOR_SIZE,
  },
  [`&:disabled .${selectors.handle}`]: {
    cursor: 'not-allowed',
  },

  [`.${selectors.label}`]: screenreaderOnly,

  [`.${selectors.tray}`]: {
    backgroundColor: `var(${ThemedColors.Surface3})`,
    borderRadius: TRAY_BLOCK_SIZE / 2,
    blockSize: TRAY_BLOCK_SIZE,
    display: 'flex',
    inlineSize: TRAY_INLINE_SIZE,
    ...Transition(['backgroundColor']),
  },
  [`&[aria-checked=true] .${selectors.tray}`]: {
    backgroundColor: `var(${ThemedColors.Primary5})`,
  },
})

export const containerState = (theme: Theme) =>
  ({
    // The ring is for the keyboard: a pointer already knows where it pressed,
    // and a switch keeps focus after a click.
    [`&:focus-visible .${selectors.handle}`]: FocusIndicator[theme],
  }) satisfies CSSObject
